/**
 * lp-vanilla-modal.js
 * Lightweight vanilla-JS modal system replacing Bootstrap's modal plugin.
 * Supports: show / hide / static-backdrop / fade / data-dismiss / events
 *
 * Public API (mirrors Bootstrap's modal interface):
 *   lpModal.show(el, opts)   – show a modal (el = DOM element or selector string)
 *   lpModal.hide(el)         – hide a modal
 *   lpModal.hideAll()        – hide every open modal
 *
 * Events dispatched on the modal element:
 *   'show.bs.modal'    – just before the modal becomes visible
 *   'shown.bs.modal'   – after transition completes
 *   'hide.bs.modal'    – just before the modal starts hiding
 *   'hidden.bs.modal'  – after the modal is fully hidden
 *
 * Opts:
 *   { backdrop: 'static' | true, keyboard: true | false }
 *
 * Auto-wired:
 *   - [data-dismiss="modal"]  → closes the closest .modal
 *   - [data-toggle="modal"]   → opens the target modal
 */
(function () {
  "use strict";

  var TRANSITION_MS = 150; // matches Bootstrap .fade duration
  var activeModals = [];

  // ---- Helpers ----
  function getEl(ref) {
    if (!ref) return null;
    if (typeof ref === "string") return document.querySelector(ref);
    return ref;
  }

  function dispatch(el, name) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  }

  // ---- Backdrop ----
  function getOrCreateBackdrop() {
    var bd = document.querySelector(".lp-modal-backdrop");
    if (!bd) {
      bd = document.createElement("div");
      bd.className = "lp-modal-backdrop";
      bd.style.cssText =
        "position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1040;opacity:0;transition:opacity .15s ease";
      document.body.appendChild(bd);
    }
    return bd;
  }

  function showBackdrop() {
    var bd = getOrCreateBackdrop();
    bd.style.display = "block";
    bd.offsetHeight; // force reflow
    bd.style.opacity = "1";
  }

  function hideBackdrop() {
    var bd = document.querySelector(".lp-modal-backdrop");
    if (!bd) return;
    bd.style.opacity = "0";
    setTimeout(function () {
      bd.style.display = "none";
    }, TRANSITION_MS);
  }

  // ---- Show ----
  function show(ref, opts) {
    var el = getEl(ref);
    if (!el) return;

    opts = opts || {};
    el._lpModalOpts = opts;

    dispatch(el, "show.bs.modal");

    // Body scroll lock
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");

    showBackdrop();

    el.style.display = "block";
    el.setAttribute("aria-modal", "true");
    el.setAttribute("role", "dialog");
    el.offsetHeight; // force reflow
    el.classList.add("show");

    // Track
    if (activeModals.indexOf(el) === -1) activeModals.push(el);

    setTimeout(function () {
      dispatch(el, "shown.bs.modal");
    }, TRANSITION_MS);

    // Backdrop click → close (unless 'static')
    el._lpBackdropClick = function (e) {
      if (e.target === el) {
        if (opts.backdrop === "static") return;
        hide(el);
      }
    };
    el.addEventListener("click", el._lpBackdropClick);

    // Escape key
    if (opts.keyboard !== false) {
      el._lpEscHandler = function (e) {
        if (e.key === "Escape") hide(el);
      };
      document.addEventListener("keydown", el._lpEscHandler);
    }
  }

  // ---- Hide ----
  function hide(ref) {
    var el = getEl(ref);
    if (!el || !el.classList.contains("show")) return;

    dispatch(el, "hide.bs.modal");

    el.classList.remove("show");

    // Cleanup listeners
    if (el._lpBackdropClick) {
      el.removeEventListener("click", el._lpBackdropClick);
      el._lpBackdropClick = null;
    }
    if (el._lpEscHandler) {
      document.removeEventListener("keydown", el._lpEscHandler);
      el._lpEscHandler = null;
    }

    // Remove from active list
    var idx = activeModals.indexOf(el);
    if (idx > -1) activeModals.splice(idx, 1);

    setTimeout(function () {
      el.style.display = "none";
      el.removeAttribute("aria-modal");

      // Only unlock body if no more modals open
      if (activeModals.length === 0) {
        document.body.style.overflow = "";
        document.body.classList.remove("modal-open");
        hideBackdrop();
      }

      dispatch(el, "hidden.bs.modal");
    }, TRANSITION_MS);
  }

  function hideAll() {
    // copy array since hide mutates it
    activeModals.slice().forEach(function (el) {
      hide(el);
    });
  }

  // ---- Auto-wire data-dismiss="modal" ----
  document.addEventListener("click", function (e) {
    // data-dismiss="modal"
    var dismissBtn = e.target.closest('[data-dismiss="modal"]');
    if (dismissBtn) {
      var modal = dismissBtn.closest(".modal");
      if (modal) {
        e.preventDefault();
        hide(modal);
        return;
      }
    }

    // data-toggle="modal" data-target="#id"
    var toggleBtn = e.target.closest('[data-toggle="modal"]');
    if (toggleBtn) {
      var target = toggleBtn.getAttribute("data-target") || toggleBtn.getAttribute("href");
      if (target) {
        e.preventDefault();
        show(target);
      }
    }
  });

  // ---- Public API ----
  window.lpModal = {
    show: show,
    hide: hide,
    hideAll: hideAll,
    activeModals: activeModals
  };
})();
